"use client";

import { useRouter } from "next/navigation";
import { useCallback, useEffect, useState } from "react";

class UnauthorizedError extends Error {}

export async function api<T>(url: string, init?: RequestInit & { json?: unknown }): Promise<T> {
  const res = await fetch(url, {
    ...init,
    headers: { ...(init?.json !== undefined ? { "Content-Type": "application/json" } : {}), ...init?.headers },
    body: init?.json !== undefined ? JSON.stringify(init.json) : init?.body,
  });
  const data = await res.json().catch(() => ({}));
  if (res.status === 401) throw new UnauthorizedError((data as { error?: string }).error ?? "Please sign in");
  if (!res.ok) throw new Error((data as { error?: string }).error ?? `Request failed (${res.status})`);
  return data as T;
}

/**
 * Last answer per url for hooks that opt in (`{ keep: true }`): switching back to a market shows its last view at
 * once while the fresh one loads (market tabs). A per-session convenience in memory, never persisted.
 */
const lastByUrl = new Map<string, unknown>();
const LAST_MAX = 60;

/** Keeps a url's last answer (the newest at the end; the oldest dropped beyond `LAST_MAX`). */
function remember(url: string, data: unknown): void {
  lastByUrl.delete(url);
  lastByUrl.set(url, data);
  if (lastByUrl.size > LAST_MAX) lastByUrl.delete(lastByUrl.keys().next().value!);
}

interface State<T> {
  url: string | null;
  /** The view the answer belongs to (`same`). */
  same: string | null;
  version: number;
  data: T | null;
  error: string | null;
}

/**
 * GET-with-state hook. `loading` is derived (a request for the current url/version is in
 * flight); results for an outdated url are ignored. `reload` refetches in place. `same`: the view a url shows (e.g. a
 * market and its timeframe) — while a new url of the same view loads (another choice for it), the last answer stays.
 */
export function useApi<T>(url: string | null, opts: { keep?: boolean; same?: string } = {}) {
  const router = useRouter();
  const [version, setVersion] = useState(0);
  const [state, setState] = useState<State<T>>({ url: null, same: null, version: -1, data: null, error: null });
  const same = opts.same ?? null;

  useEffect(() => {
    if (!url) return;
    let cancelled = false;
    const arrived = (data: T) => {
      if (opts.keep) remember(url, data);
      if (!cancelled) setState({ url, same, version, data, error: null });
    };
    const failed = (e: unknown) => {
      if (cancelled) return;
      if (e instanceof UnauthorizedError) router.push("/login");
      setState((s) => ({ url, same, version, data: s.url === url ? s.data : null, error: e instanceof Error ? e.message : String(e) }));
    };
    api<T>(url).then(arrived).catch(failed);
    return () => {
      cancelled = true;
    };
  }, [url, same, version, router, opts.keep]);

  const reload = useCallback(() => setVersion((v) => v + 1), []);
  const setData = useCallback((data: T) => setState((s) => ({ ...s, data })), []);
  const current = state.url === url;
  // keep: a url seen before shows its last answer until the new one arrives; else the same view's last answer
  const seen = opts.keep && url ? ((lastByUrl.get(url) as T | undefined) ?? null) : null;
  const sameView = same !== null && state.same === same ? state.data : null;
  const kept = current ? null : (seen ?? sameView);
  return {
    // Keep showing the previous data for the same url while it refreshes.
    data: current ? state.data : kept,
    error: current ? state.error : null,
    loading: url !== null && (!current || state.version !== version),
    reload,
    setData,
  };
}
